import React from "react";
import { Box, Container, Grid, Typography } from "@material-ui/core";
import QRCode from "react-qr-code";

import { USMQRCodeSize } from "../../utils/constants";
import { SmallQuantityTicket } from "../../interface/api/SmallQuantityTicket";
import { PrintableTicketProps } from "../../interface/PrintableTicketProps";
import UnitedLogo from "../../assets/logo.png";
import { formatDate, formatDateTime, formatTime } from "../../utils/date-helper";


/**
 * The printed small-quantity ticket, read-only. Rendered both by the staff-only
 * /small-quantity-digital-ticket/:id route and, once a ticket is received, to
 * drivers who scan its QR code.
 */
const SmallQuantityPrintableTicket = ({
  ticket,
  ticketUrl,
  haulRecordUrl,
}: PrintableTicketProps<SmallQuantityTicket>) => {
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          border: "2px solid black",
          padding: "20px",
          marginTop: "20px",
          marginBottom: "20px",
        }}
        style={{ backgroundColor: "#e3f2fd" }}
      >
        {ticket.isReceived && (
          <Box
            style={{ backgroundColor: "green" }}
            sx={{
              border: "3px solid green",
              borderRadius: 6,
              textAlign: "center",
              padding: 5,
              marginBottom: 20,
            }}
          >
            <Typography
              style={{
                fontSize: 38,
                textTransform: "uppercase",
                letterSpacing: 5,
                margin: 0,
                padding: 0,
                color: "white",
              }}
            >
              Received
            </Typography>
          </Box>
        )}
        {/* Top stub — LOADING SITE PORTION, carries the haul-record QR */}
        <Box sx={{ marginBottom: 20 }}>
          <Typography variant="h5" style={{ marginBottom: 4 }}>
            <b>United</b> Soils Management Ltd.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>14395 Ninth Line, Stouffville, ON L4A 8B3</Typography>
            <Typography variant="h6" style={{ color: "red" }}>
              No. {ticket.ticketNumber}
            </Typography>
          </Box>
          <Typography
            style={{ textDecoration: "underline", marginTop: 8, marginBottom: 8 }}
          >
            LOADING SITE PORTION
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: 20,
              marginBottom: 20,
            }}
          >
            {haulRecordUrl && (
              <Box sx={{ marginRight: 40 }}>
                <QRCode value={haulRecordUrl} size={USMQRCodeSize} />
              </Box>
            )}
            <Box>
              <Typography style={{ fontWeight: 600 }}>HAUL RECORD</Typography>
              {ticket.contractorCompanyName && (
                <Typography style={{ marginTop: 8 }}>
                  <b>Contractor:</b> {ticket.contractorCompanyName}
                </Typography>
              )}
              {ticket.jobSite && (
                <Typography>
                  <b>Job Site:</b> {ticket.jobSite}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Tear-off separator */}
        <div style={{ border: "1px dashed black" }} />

        {/* Header — RECEIVING SITE PORTION */}
        <Typography variant="h5" style={{ marginTop: 20, marginBottom: 4 }}>
          <b>United</b> Soils Management Ltd.
        </Typography>
        <Typography
          variant="h6"
          style={{ fontWeight: 600, marginBottom: 15 }}
        >
          SMALL QUANTITIES
        </Typography>
        <Typography variant="h5" style={{ color: "red", marginBottom: 15 }}>
          No. {ticket.ticketNumber}
        </Typography>
        <Grid container spacing={3} style={{ marginBottom: 10 }}>
          <Grid item xs={6}>
            {ticketUrl && (
              <QRCode value={ticketUrl} size={USMQRCodeSize} />
            )}
            <img
              src={UnitedLogo}
              width={125}
              style={{ marginTop: 30 }}
              alt="United Logo"
            />
          </Grid>
        </Grid>

        {/* Basic Info */}
        <Box sx={{ marginBottom: 20 }}>
          <Typography style={{ marginBottom: 12 }}>
            <b>Date:</b> {ticket.timeLoaded ? formatDate(ticket.timeLoaded, false) : formatDate(ticket.date, false)}
          </Typography>
          {ticket.contractorCompanyName && (
            <Typography style={{ marginBottom: 12 }}>
              <b>Contractor:</b> {ticket.contractorCompanyName}
            </Typography>
          )}
          {ticket.jobSite && (
            <Typography style={{ marginBottom: 12 }}>
              <b>Job Site:</b> {ticket.jobSite}
            </Typography>
          )}
          <Typography style={{ textAlign: "center", marginBottom: 8 }}>
            9th LINE FILL SITE
          </Typography>
        </Box>

        {/* Loading Site */}
        <Box sx={{ marginBottom: 20 }}>
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              textDecoration: "underline",
              marginBottom: 12,
            }}
          >
            LOADING SITE
          </Typography>
          {ticket.timeLoaded && (
            <Typography style={{ marginBottom: 12 }}>
              <b>Time Loaded:</b> {formatTime(ticket.timeLoaded, false)}
            </Typography>
          )}
          {(ticket.truckNumber || ticket.truckLicensePlate) && (
            <Typography style={{ marginBottom: 12 }}>
              <b>Truck No./Plate No.:</b> {ticket.truckNumber} /{" "}
              {ticket.truckLicensePlate}
            </Typography>
          )}
          {ticket.truckDriverName && (
            <Typography style={{ marginBottom: 12 }}>
              <b>Driver Name:</b> {ticket.truckDriverName}
            </Typography>
          )}
          {ticket.haulageCompanyName && (
            <Typography style={{ marginBottom: 12 }}>
              <b>Haulage Co.:</b> {ticket.haulageCompanyName}
            </Typography>
          )}
          <Typography
            style={{
              marginBottom: 12,
              fontStyle: "italic",
              textAlign: "center",
              fontSize: "0.85rem",
            }}
          >
            I declare by signing below that I have witnessed the loading of the
            described truck with clean fill free of contamination.
          </Typography>
        </Box>

        {/* No Refund Banner */}
        <Box sx={{ marginBottom: 20 }}>
          <Typography
            variant="h6"
            style={{
              marginBottom: 12,
              marginTop: 20,
              color: "red",
              textAlign: "center",
            }}
          >
            No Refund - Non Transferable
          </Typography>
        </Box>

        {/* Receiving Site */}
        <Box sx={{ marginBottom: 20 }}>
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              textDecoration: "underline",
              marginBottom: 12,
            }}
          >
            Receiving Site Information
          </Typography>
          {ticket.timeReceived && (
            <Typography style={{ marginBottom: 12 }}>
              <b>Time Received:</b> {formatDateTime(ticket.timeReceived, false)}
            </Typography>
          )}
          <Typography style={{ marginBottom: 12 }}>
            <b>Address:</b> 14395 Ninth Line, Stouffville, ON L4A 8B3
          </Typography>
          <Typography style={{ marginBottom: 12 }}>
            By registering this ticket, United Soils acknowledges the placement
            of the excess soil.
          </Typography>
        </Box>

        {/* Tear-off separator */}
        <div style={{ border: "1px dashed black" }} />

        {/* Bottom stub — TRANSPORT COMPANY PORTION, carries the haul-record QR */}
        <Box sx={{ marginTop: 20 }}>
          <Typography variant="h5" style={{ marginBottom: 20 }}>
            <b>United</b> Soils Management Ltd.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>14395 Ninth Line, Stouffville, ON L4A 8B3</Typography>
            <Typography variant="h6" style={{ color: "red" }}>
              No. {ticket.ticketNumber}
            </Typography>
          </Box>
          <Typography
            style={{ textDecoration: "underline", marginTop: 8 }}
          >
            TRANSPORT COMPANY PORTION
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: 20,
              marginBottom: 20,
            }}
          >
            {haulRecordUrl && (
              <Box sx={{ marginRight: 40 }}>
                <QRCode value={haulRecordUrl} size={USMQRCodeSize} />
              </Box>
            )}
            <Box>
              <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
                HAUL RECORD
              </Typography>
              {ticket.contractorCompanyName && (
                <Typography style={{ marginBottom: 8 }}>
                  <b>Contractor:</b> {ticket.contractorCompanyName}
                </Typography>
              )}
              {ticket.jobSite && (
                <Typography>
                  <b>Job Site:</b> {ticket.jobSite}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default SmallQuantityPrintableTicket;
